import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Check, ChevronsUpDown, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { OrganisationsFilterCombobox } from '@/features/users/components/OrganisationsFilterCombobox';
import { useAnomalies, useAnomalySummary, useLatestForecast, useForecastableSensors, useTriggerForecast } from '../hooks';
import ForecastChart from '../components/ForecastChart';
import AnomalyTable from '../components/AnomalyTable';
import ForecastSummaryCards from '../components/ForecastSummaryCard';
import type { ForecastChartPoint } from '../types';

/**
 * Build chart data from forecast points: combine actual values with forecast predictions.
 */
function buildChartData(
  forecasts: Array<{
    forecast_timestamp: string;
    value_mean: number;
    value_q10: number;
    value_q90: number;
    actual_value: number | null;
    is_anomaly: number | null;
  }>,
): ForecastChartPoint[] {
  return forecasts.map(f => {
    const date = new Date(f.forecast_timestamp);
    return {
      timestamp: f.forecast_timestamp,
      label: `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`,
      actual: f.actual_value,
      forecast_mean: f.value_mean,
      forecast_q10: f.value_q10,
      forecast_q90: f.value_q90,
      bandRange: f.value_q10 != null && f.value_q90 != null ? [f.value_q10, f.value_q90] : null,
      is_anomaly: f.is_anomaly === 1,
    };
  });
}

export default function ForecastDashboardPage() {
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<string | undefined>();
  const [sensorComboOpen, setSensorComboOpen] = useState(false);
  const [sensorSearch, setSensorSearch] = useState('');
  const [selectedSensor, setSelectedSensor] = useState<{ id: string; name: string } | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>('all');

  const organisationId = selectedOrganisationId && selectedOrganisationId !== 'all'
    ? selectedOrganisationId
    : undefined;
  const debouncedSearch = useDebounce(sensorSearch, 300);
  const { data: sensorResults, isLoading: sensorsLoading, isError: sensorsError } =
    useForecastableSensors(organisationId, debouncedSearch);

  const lookupSensorId = selectedSensor?.id ?? '';

  const { data: forecasts, isLoading: forecastLoading, isError: forecastError, refetch: refetchForecast } =
    useLatestForecast(lookupSensorId, organisationId);
  const { data: anomalies, isLoading: anomaliesLoading, isError: anomaliesError, refetch: refetchAnomalies } =
    useAnomalies({
      organisationId,
      severity: severityFilter !== 'all' ? severityFilter : undefined,
      pageLength: 50,
    }, { enabled: !!organisationId });
  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } =
    useAnomalySummary(organisationId ? { organisationId } : undefined, { enabled: !!organisationId });

  const triggerMutation = useTriggerForecast();

  const chartData = forecasts ? buildChartData(forecasts) : [];

  const handleOrganisationChange = (id: string) => {
    setSelectedOrganisationId(id === 'all' ? undefined : id);
    setSelectedSensor(null);
    setSensorSearch('');
    setSensorComboOpen(false);
  };

  const handleSensorSelect = (id: string, name: string) => {
    setSelectedSensor({ id, name });
    setSensorComboOpen(false);
    setSensorSearch('');
  };

  const handleTrigger = () => {
    if (lookupSensorId && organisationId) {
      triggerMutation.mutate({ sensorId: lookupSensorId, organisationId });
    }
  };

  const handleRefresh = () => {
    if (lookupSensorId && organisationId) refetchForecast();
    if (organisationId) {
      refetchAnomalies();
      refetchSummary();
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold">Forecast Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            TimesFM sensor forecasting and anomaly detection
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <ForecastSummaryCards summary={summary} isLoading={summaryLoading} />

      {/* Sensor Forecast Lookup */}
      <div className="border rounded-lg p-4 space-y-4">
        <h2 className="text-lg font-medium">Sensor Forecast</h2>
        <div className="grid gap-3 lg:grid-cols-[minmax(240px,320px)_minmax(280px,420px)_auto] lg:items-end">
          <div>
            <span className="text-sm font-medium">Organisation</span>
            <OrganisationsFilterCombobox
              value={selectedOrganisationId ?? 'all'}
              onChange={handleOrganisationChange}
              placeholder="Select organisation"
              className="mt-1 h-10 w-full text-sm"
            />
          </div>
          <div>
            <span className="text-sm font-medium">Sensor</span>
            <Popover open={sensorComboOpen} onOpenChange={setSensorComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={sensorComboOpen}
                  disabled={!organisationId}
                  className="h-10 w-full justify-between font-normal mt-1"
                >
                  <span className="truncate">
                    {selectedSensor
                      ? `${selectedSensor.name} (${selectedSensor.id.slice(0, 8)}...)`
                      : organisationId
                        ? 'Select sensor...'
                        : 'Select organisation first'}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[420px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Filter sensors..."
                    value={sensorSearch}
                    onValueChange={setSensorSearch}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {!organisationId
                        ? 'Select an organisation first'
                        : sensorsError
                          ? 'Failed to load sensors'
                          : sensorsLoading
                            ? 'Loading sensors...'
                            : 'No forecastable sensors found'}
                    </CommandEmpty>
                    {sensorResults && sensorResults.length > 0 && (
                      <CommandGroup>
                        {sensorResults.map(sensor => (
                          <CommandItem
                            key={sensor._id}
                            value={sensor._id}
                            onSelect={() => handleSensorSelect(sensor._id, sensor.name)}
                          >
                            <Check className={cn('mr-2 h-4 w-4', selectedSensor?.id === sensor._id ? 'opacity-100' : 'opacity-0')} />
                            <div className="flex flex-col">
                              <span className="text-sm">{sensor.name}</span>
                              <span className="text-xs text-muted-foreground">
                                {[sensor.stream_name, sensor.customid, sensor._id].filter(Boolean).join(' | ')}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          {lookupSensorId && (
            <Button
              variant="outline"
              onClick={handleTrigger}
              disabled={triggerMutation.isPending || !organisationId}
              className="h-10"
            >
              {triggerMutation.isPending ? 'Generating...' : 'Generate New'}
            </Button>
          )}
        </div>

        {/* Forecast Chart */}
        {!organisationId ? (
          <div className="h-[160px] flex items-center justify-center text-muted-foreground border rounded-lg">
            Select an organisation to load forecastable sensors.
          </div>
        ) : lookupSensorId ? (
          <div className="mt-4">
            {forecastLoading ? (
              <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                Loading forecast data...
              </div>
            ) : forecastError ? (
              <div className="h-[200px] flex items-center justify-center text-destructive border border-destructive/20 rounded-lg">
                Failed to load forecast data. Please try again.
              </div>
            ) : chartData.length > 0 ? (
              <ForecastChart
                data={chartData}
                title={`Forecast for ${selectedSensor?.name ?? lookupSensorId}`}
              />
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground border rounded-lg">
                No forecast data available for this sensor. Click "Generate New" to create one.
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Anomaly Events */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium">Recent Anomalies</h2>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {!organisationId ? (
          <div className="border rounded-lg p-8 text-center text-muted-foreground">
            Select an organisation to view anomalies.
          </div>
        ) : anomaliesError ? (
          <div className="border border-destructive/20 rounded-lg p-8 text-center text-destructive">
            Failed to load anomalies. Please try refreshing.
          </div>
        ) : (
          <AnomalyTable
            anomalies={anomalies || []}
            isLoading={anomaliesLoading}
          />
        )}
      </div>
    </div>
  );
}
